import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import API_BASE_URL from "../../../../Constants";
import NavHeightAdjust from "../../../../components/navbar/NavHeightAdjust";

export default function InspectionWizard() {
  const navigate = useNavigate();

  // Read buildingId & agencyId from URL like ?buildingId=1&agencyId=1002
  const [searchParams] = useSearchParams();
  const buildingIdParam = searchParams.get("buildingId") || "";
  const agencyIdParam = searchParams.get("agencyId") || "";

  // Wizard steps: 1..5
  const [step, setStep] = useState(1);

  // -------------
  // Wizard state
  // -------------
  const [buildingId, setBuildingId] = useState(buildingIdParam);
  const [agencyId, setAgencyId] = useState(agencyIdParam);

  // We'll fetch these so user can pick (similar to your “BuildingInspectionsEntryPage”)
  const [devices, setDevices] = useState([]);
  const [managementCompanies, setManagementCompanies] = useState([]);
  const [agencyTests, setAgencyTests] = useState([]);

  // Form fields
  const [selectedDeviceIds, setSelectedDeviceIds] = useState([]);
  const [managementCompanyId, setManagementCompanyId] = useState("");
  const [testId, setTestId] = useState("");
  const [taskType, setTaskType] = useState("perform");
  const [approvalDate, setApprovalDate] = useState("");
  const [inspYear, setInspYear] = useState("");
  const [hours, setHours] = useState("");
  const [servicer, setServicer] = useState("");
  const [nextDate, setNextDate] = useState("");
  const [lastDate, setLastDate] = useState("");

  // For validation
  const [error, setError] = useState("");

  // On mount => fetch devices, mgmt cos, agency tests
  useEffect(() => {
    // 1) fetch devices for this building
    const fetchDevices = async () => {
      if (!buildingId) return;
      try {
        const res = await fetch(
          `${API_BASE_URL}/api/setup/buildings/devices/${buildingId}`,
          {
            credentials: "include",
          }
        );
        if (!res.ok) throw new Error(`Devices fetch error: ${res.status}`);
        const data = await res.json();
        setDevices(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error(err);
        setDevices([]);
      }
    };

    // 2) fetch mgmt cos
    const fetchMgmtCos = async () => {
      try {
        const res = await fetch(
          `${API_BASE_URL}/api/setup/managementcompanies/all`,
          {
            credentials: "include",
          }
        );
        if (!res.ok) throw new Error(`Mgmt cos error: ${res.status}`);
        const data = await res.json();
        setManagementCompanies(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error(err);
        setManagementCompanies([]);
      }
    };

    // 3) fetch agency tests
    const fetchAgencyTests = async () => {
      if (!agencyId) return;
      try {
        const res = await fetch(
          `${API_BASE_URL}/api/inspections/agency/agencyTestsForAgency/${agencyId}`,
          { credentials: "include" }
        );
        if (!res.ok) throw new Error(`Agency tests error: ${res.status}`);
        const data = await res.json();
        setAgencyTests(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error(err);
        setAgencyTests([]);
      }
    };

    fetchDevices();
    fetchMgmtCos();
    fetchAgencyTests();
  }, [buildingId, agencyId]);

  // Next/Back Step
  const nextStep = () => {
    if (step < 5) setStep(step + 1);
    else handleSubmit();
  };
  const prevStep = () => {
    if (step > 1) setStep(step - 1);
    else navigate("/inspections-violations"); // or some fallback route
  };

  const handleSubmit = async () => {
    // ...

    // Instead of sending deviceIds: [1,2],
    // build an array of objects: inspectionDevices: [ { device: { deviceId: ... } }, ... ]
    const inspectionDevices = selectedDeviceIds.map((id) => ({
      device: { deviceId: Number(id) },
    }));

    const payload = {
      buildingId: Number(buildingId),
      managementCompanyId: Number(managementCompanyId),
      testId: testId ? Number(testId) : null,
      taskType,
      approvalDate,
      inspYear: parseInt(inspYear, 10) || 0,
      hours: parseInt(hours, 10) || 0,
      servicer,
      nextDate,
      lastDate,
      status: "Active",

      // This is crucial:
      inspectionDevices,
    };

    try {
      const res = await fetch(`${API_BASE_URL}/api/inspections`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        throw new Error(`Create inspection error: ${res.status}`);
      }
      alert("Inspection created successfully via wizard!");
      navigate("/setup/buildings");
    } catch (err) {
      console.error(err);
      alert(`Error creating inspection: ${err.message}`);
    }
  };

  // For device checkboxes
  const handleDeviceCheckbox = (devId, checked) => {
    setSelectedDeviceIds((prev) => {
      return checked ? [...prev, devId] : prev.filter((d) => d !== devId);
    });
  };

  // Return different step UIs
  return (
    <NavHeightAdjust>
      <div className="p-4 max-w-2xl mx-auto bg-white shadow rounded">
        <h1 className="text-2xl font-bold mb-2">
          Inspection Wizard (Step {step})
        </h1>

        {step === 1 && (
          <div>
            <h2 className="text-xl font-semibold mb-4">
              Select Management Co & Agency Test
            </h2>
            {/* Management Company */}
            <label className="block mb-2">Management Company (required)</label>
            <select
              className="border p-1 mb-4"
              value={managementCompanyId}
              onChange={(e) => setManagementCompanyId(e.target.value)}
            >
              <option value="">-- Select --</option>
              {managementCompanies.map((mc) => (
                <option key={mc.managementId} value={mc.managementId}>
                  {mc.companyName}
                </option>
              ))}
            </select>

            {/* Agency Test */}
            <label className="block mb-2">Agency Test (required)</label>
            <select
              className="border p-1 mb-4"
              value={testId}
              onChange={(e) => setTestId(e.target.value)}
            >
              <option value="">-- Select Test --</option>
              {agencyTests.map((t) => (
                <option key={t.testId} value={t.testId}>
                  {t.label || `Test #${t.testId}`}
                </option>
              ))}
            </select>
          </div>
        )}

        {step === 2 && (
          <div>
            <h2 className="text-xl font-semibold mb-4">Inspection Details</h2>
            <label className="block mb-2">Task Type (required)</label>
            <select
              className="border p-1 mb-4"
              value={taskType}
              onChange={(e) => setTaskType(e.target.value)}
            >
              <option value="perform">Perform</option>
              <option value="witness">Witness</option>
              <option value="both">Both</option>
            </select>

            <label className="block mb-2">Approval Date (required)</label>
            <input
              type="date"
              className="border p-1 mb-4 w-full"
              value={approvalDate}
              onChange={(e) => setApprovalDate(e.target.value)}
            />

            <label className="block mb-2">Inspection Year (required)</label>
            <input
              type="number"
              className="border p-1 mb-4 w-full"
              value={inspYear}
              onChange={(e) => setInspYear(e.target.value)}
            />
          </div>
        )}

        {step === 3 && (
          <div>
            <h2 className="text-xl font-semibold mb-4">
              Additional Fields (Hours, Servicer, etc.)
            </h2>
            <label className="block mb-2">Hours</label>
            <input
              type="number"
              className="border p-1 mb-4 w-full"
              value={hours}
              onChange={(e) => setHours(e.target.value)}
            />

            <label className="block mb-2">Servicer</label>
            <input
              type="text"
              className="border p-1 mb-4 w-full"
              value={servicer}
              onChange={(e) => setServicer(e.target.value)}
            />

            <label className="block mb-2">Next Date</label>
            <input
              type="date"
              className="border p-1 mb-4 w-full"
              value={nextDate}
              onChange={(e) => setNextDate(e.target.value)}
            />

            <label className="block mb-2">Last Date</label>
            <input
              type="date"
              className="border p-1 w-full"
              value={lastDate}
              onChange={(e) => setLastDate(e.target.value)}
            />
          </div>
        )}

        {step === 4 && (
          <div>
            <h2 className="text-xl font-semibold mb-4">Select Device(s)</h2>
            {devices.length === 0 ? (
              <p>No devices found for this building.</p>
            ) : (
              <div className="border p-2 max-h-40 overflow-auto">
                {devices.map((dev) => {
                  const isChecked = selectedDeviceIds.includes(dev.deviceId);
                  return (
                    <label key={dev.deviceId} className="block mb-2">
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={(e) =>
                          handleDeviceCheckbox(dev.deviceId, e.target.checked)
                        }
                      />
                      <span className="ml-2">
                        Device #{dev.deviceId} - {dev.deviceType || "Unknown"}
                      </span>
                    </label>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {step === 5 && (
          <div>
            <h2 className="text-xl font-semibold mb-4">Review & Confirm</h2>
            <p>
              Building ID: <strong>{buildingId}</strong>
              Agency ID: <strong>{agencyId}</strong>
            </p>
            <p>Mgmt Co: {managementCompanyId}</p>
            <p>Test ID: {testId}</p>
            <p>Task Type: {taskType}</p>
            <p>Approval Date: {approvalDate}</p>
            <p>Insp Year: {inspYear}</p>
            <p>Hours: {hours}</p>
            <p>Servicer: {servicer}</p>
            <p>Next Date: {nextDate}</p>
            <p>Last Date: {lastDate}</p>
            <p>Device(s): {selectedDeviceIds.join(", ") || "None"}</p>
          </div>
        )}

        {error && <p className="text-red-600 text-sm mt-2">{error}</p>}

        {/* Wizard Buttons */}
        <div className="mt-6 flex justify-between">
          <button
            onClick={prevStep}
            className="px-4 py-2 bg-gray-300 text-gray-800 font-bold rounded hover:bg-gray-400"
          >
            {step === 1 ? "Cancel" : "Back"}
          </button>
          <button
            onClick={nextStep}
            className="px-4 py-2 bg-blue-600 text-white font-bold rounded hover:bg-blue-700"
          >
            {step < 5 ? "Next" : "Finish"}
          </button>
        </div>
      </div>
    </NavHeightAdjust>
  );
}
